import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";

const AuthContext = createContext(null);

// The LeadOrbit Android app reads localStorage "token" + "user" to run its background
// connection (WhatsApp call rings, alerts, follow-ups) while the app is closed.
// Inside the app the 12 h web login is swapped for a 45-day app login so the phone
// keeps ringing overnight; logout still ends it.
const IN_ANDROID_APP = typeof navigator !== "undefined" && /; wv\)/.test(navigator.userAgent || "");

async function syncAppSession(u) {
  if (!u || !u.id) return;
  try { localStorage.setItem("user", JSON.stringify({ id: u.id, name: u.name || "" })); } catch (e) { /* ignore */ }
  if (!IN_ANDROID_APP) return;
  try {
    const t = localStorage.getItem("token");
    if (!t) return;
    const part = t.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const exp = (JSON.parse(atob(part)).exp || 0) * 1000;
    if (exp - Date.now() > 7 * 24 * 3600 * 1000) return;   // already a long app login
    const { data } = await api.post("/auth/app-token");
    if (data?.token) localStorage.setItem("token", data.token);
  } catch (e) { /* keep the normal login */ }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = anon
  const [error, setError] = useState("");
  const [isAttendanceLocked, setAttendanceLocked] = useState(false);
  const leaveNoticeRef = useRef(false);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      syncAppSession(data);
      setAttendanceLocked(false);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    const t = localStorage.getItem("token");
    if (!t) {
      setUser(false);
      return;
    }
    fetchMe();
  }, [fetchMe]);

  // Global soft-logout interceptor: if any request returns 401 with a
  // `user_on_leave` code (or the user has been deactivated), clear the token
  // and redirect to the login screen. Intercepts 403 attendance lockout as well.
  useEffect(() => {
    const id = api.interceptors.response.use(
      (r) => r,
      (err) => {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        const code = typeof detail === "object" ? detail?.code : null;
        
        if (status === 401 && localStorage.getItem("token")) {
          if (!leaveNoticeRef.current) {
            leaveNoticeRef.current = true;
            if (code === "user_on_leave" || code === "attendance_required") {
              const msg = (typeof detail === "object" && detail?.message)
                || (code === "attendance_required"
                  ? "Punch in at the office device to use the CRM."
                  : "You are on leave — access disabled.");
              toast.error(msg, { duration: 8000 });
            } else {
              toast.error("Session expired — please log in again.");
            }
            setTimeout(() => { leaveNoticeRef.current = false; }, 4000);
          }
          localStorage.removeItem("token");
          setUser(false);
          setAttendanceLocked(false);
          if (window.location.pathname !== "/login") {
            window.location.href = "/login";
          }
        } else if (status === 403 && code === "attendance_required") {
          setAttendanceLocked(true);
        }
        return Promise.reject(err);
      },
    );
    return () => { api.interceptors.response.eject(id); };
  }, []);

  const login = async (username, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { username, password });
      localStorage.setItem("token", data.token);
      setUser(data.user);
      syncAppSession(data.user);
      setAttendanceLocked(false);
      return true;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.code : null;
      if (code === "user_on_leave" || code === "attendance_required") {
        const msg = (typeof detail === "object" && detail?.message)
          || "Punch in at the office device before logging in.";
        setError(msg);
      } else {
        setError(errMsg(e, "Login failed"));
      }
      return false;
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(false);
    setAttendanceLocked(false);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, error, refresh: fetchMe, isAttendanceLocked, setAttendanceLocked }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
